"use client";

import React, { useState } from 'react';
import { Check, Loader2, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/mixpanel';
import { useAppSelector } from '@/app/store/hooks';
import { Modal } from '@/components/ui/modal';

import type { ProductType } from '@/types/cart.types';

interface PlanCardProps {
    id?: string;
    title: string;
    description: string;
    image: string;
    features: string[];
    badge?: string;
    onBuy: () => void;
    isLoading?: boolean;
    cardClassName?: string;
    previewPdf?: string;
    onPreviewClick?: () => void;
}

const PlanCard = ({ id, title, description, image, features, badge, onBuy, isLoading, cardClassName, previewPdf, onPreviewClick }: PlanCardProps) => (
    <div id={id} className={cn(
        "rounded-[16px] border border-slate-200 flex flex-col h-full shadow-sm overflow-hidden relative",
        cardClassName || "bg-white"
    )}>
        {badge && (
            <div className="absolute -top-1 -right-1 bg-[#D9A74A] text-white text-[12px] font-bold px-3 py-2 rounded-bl-[16px] z-10">
                {badge}
            </div>
        )}

        {/* Top Section */}
        <div className="px-6 pt-8 pb-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">{title}</h2>
            <div
                className={cn(
                    "aspect-[16/10] w-full rounded-xl overflow-hidden mb-6 relative group"
                )}
            >
                <img src={image} alt={title} className="w-full h-full object-cover" />
                {previewPdf && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreviewClick?.();
                        }}
                        className="absolute cursor-pointer bottom-1 right-1 px-4 py-1 font-semibold bg-black/25 border border-white/20 text-xs hover:bg-black/60 backdrop-blur-md rounded-md flex items-center justify-center text-white transition-all duration-200 border border-white/20 shadow-lg group/btn"
                        title="View Preview"
                    >
                        View
                    </button>
                )}
            </div>
            <p className="text-[14px] text-slate-500 font-medium leading-relaxed">
                {description}
            </p>
        </div>

        {/* Features Section */}
        <div className="px-6 pb-8 pt-4 flex flex-col flex-1">
            <div className="mb-8">
                <h4 className="text-[14px] font-bold text-slate-900 mb-4">Features:</h4>
                <ul className="space-y-3">
                    {features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                            <span className="text-[13px] text-slate-600 font-medium">{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="mt-auto">
                <button 
                    onClick={onBuy}
                    disabled={isLoading}
                    className="w-full py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg shadow-md shadow-blue-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70"
                >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy Now"}
                </button>
            </div>
        </div>
    </div>
);

interface DeckPlansProps {
    onAddToCart: (productType: ProductType) => void;
}

export const DeckPlans = ({ onAddToCart }: DeckPlansProps) => {
    const [activePdf, setActivePdf] = useState<string | null>(null);

    const handleBuyAction = (productType: ProductType) => {
        trackEvent("Plan Buy Clicked", { plan: productType });
        onAddToCart(productType);
    };

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <PlanCard
                    id="fast-decks"
                    title="Fast Decks"
                    description="Structure your thoughts into clean slides quickly, with light research."
                    image="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801765494_k0i5hn1s0f_ProDeck.jpg"
                    previewPdf="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/autumn.pdf"
                    features={[
                        "Quick Research mode",
                        "Decent Templates",
                        "Research Citations & outline",
                        "Limited Upto 10 Slides",
                        "Shareable deck link",
                        "Detailed viewer analytics",
                        "Share contact details",
                        "Viewer download count",
                        "Viewer commenting access"
                    ]}
                    onBuy={() => handleBuyAction('FAST_DECK')}
                    onPreviewClick={() => setActivePdf("https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/autumn.pdf")}
                />
                <PlanCard
                    id="pro-decks"
                    title="Pro Decks"
                    description="Full-depth presentations with real research and proper citations throughout."
                    image="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775801765587_l8wmk3nhx_FastDeck.jpg"
                    previewPdf="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/one_direction.pdf"
                    features={[
                        "Deep Research mode",
                        "Premium Templates",
                        "Research Citations & outline",
                        "Limited Upto 20 Slides",
                        "Shareable deck link",
                        "Detailed viewer analytics",
                        "Share contact details",
                        "Viewer download count",
                        "Viewer commenting access"
                    ]}
                    onBuy={() => handleBuyAction('PRO_DECK')}
                    onPreviewClick={() => setActivePdf("https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/one_direction.pdf")}
                />
                <PlanCard
                    id="fundraising-pitch-deck"
                    title="Fundraising Pitch Decks"
                    description="Investor-ready pitch decks built on proven startup storytelling frameworks."
                    image="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1775831165583_moejy2lid7m_fundraising.png"
                    badge="Made for Founders"
                    cardClassName="bg-gradient-to-b from-[#FFF9E1] to-transparent"
                    previewPdf="https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/Haulix-Pitch-Deck.pdf"
                    features={[
                        "Deep Research + Company research",
                        "Market & Competitors Research",
                        "Premium Templates",
                        "Research citations",
                        "Detailed Investor Q&A",
                        "Shareable link + detailed viewer analytics",
                        "Viewer commenting & download access",
                        "Pitch Prep. Document with investor Q&A",
                        "Team Collaboration"
                    ]}
                    onBuy={() => handleBuyAction('FUNDRAISING_PITCH_DECK')}
                    onPreviewClick={() => setActivePdf("https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/Haulix-Pitch-Deck.pdf")}
                />
            </div>

            <Modal isOpen={activePdf !== null} onClose={() => setActivePdf(null)} className="max-w-5xl p-0">
                <div className="aspect-[16/10] w-full">
                    {activePdf ? (
                        <iframe src={activePdf} className="w-full h-full rounded-2xl" title="PDF Preview" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                            Preview not available yet
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};
